import { IconButton, Typography } from "@material-ui/core"
import { BrandStory } from "api"
import { trackSkipStoryOne } from "common/analytics"
import { gsap } from "gsap"
import { RefObject, useEffect } from "react"
import MoreInfoWhite from "reusable/icons/MoreInfoWhite"
import { FloatingTextVariant } from "reusable/mintStory/FloatingText"
import MintStory from "reusable/mintStory/MintStory"
import { TextDataProps } from "reusable/mintStory/types"
import styled, { keyframes } from "styled-components"
import { HEADER_ANIME_D, HEADER_ANIME_M } from "theme/shared"
import Janbo from "../../../reusable/icons/Janbo"
import KodoLeft from "../../../reusable/icons/KodoLeft"
import KodoRight from "../../../reusable/icons/KodoRight"

const API_URL = process.env.API_URL

const StyledJapCont = styled.span`
  display: flex;
  gap: 11px;
  // mobile
  ${(p) => p.theme.breakpoints.down("sm")} {
    width: 40px;
    gap: 3.5px;
    margin-bottom: -10px;
  }
`
const StyledJanboWrap = styled.span`
  ${(p) => p.theme.breakpoints.down("sm")} {
    width: 70px;
    margin-bottom: -20px;
    svg {
      width: 100%;
    }
  }
`
export const arrowDown = keyframes`
  0%,
	100% {
		transform: translateY(0);
	}

	8%,
	24%,
	40%,
	56% {
		transform: translateY(-2.5px);
	}

	16%,
	32%,
	48% {
		transform: translateY(2.5px);
	}

	64% {
		transform: translateY(2px);
	}

	72% {
		transform: translateY(-2px);
	}

  80%{
    transform: translateY(0);
  }
`

const StyledArrowDownWrap = styled(IconButton)`
  /* transform: translateY(-100%); */
  // add loop down animation to svg path with id arrow
  svg path#arrow {
    animation: ${arrowDown} 5s ease 0s infinite normal forwards;
    transition: stroke 0.3s ease-in-out;
  }
  svg rect {
    transition: all 0.3s ease-in-out;
  }
  &:hover,
  &:focus {
    svg path#arrow {
      stroke: ${(p) => p?.theme.colors.black};
    }
    svg rect {
      fill: ${(p) => p?.theme.colors.white};
      stroke: ${(p) => p?.theme.colors.white};
    }
  }
`
// styled component keyframes for loop down animation

type StoryProps = {
  brandStory: BrandStory
  resetSwiper?: () => void
  scrollBtnRef: RefObject<HTMLButtonElement>
  hasCampaign: boolean
}

function Story({
  brandStory,
  resetSwiper,
  scrollBtnRef,
  hasCampaign,
}: StoryProps) {
  useEffect(() => {}, [scrollBtnRef])

  const textData: TextDataProps[] = [
    /////////////// 1. Opening
    {
      containerProps: {
        height: [280, 280],
        width: [318, 501],
        top: hasCampaign ? undefined : [20, 10],
        bottom: hasCampaign ? [175, 250] : undefined,
      },
      children: [
        {
          text: brandStory.openingTitle,
          variant: FloatingTextVariant.HEADER1,
          start: 0,
          end: 10,
        },
        {
          text: (
            <StyledArrowDownWrap
              onClick={() => {
                console.log("scroll")
                trackSkipStoryOne()
                gsap.to(window, {
                  duration: 0.7, // Duration of the scroll in seconds
                  scrollTo: {
                    y: 500, // Target element
                  },
                  ease: "power2.inOut", // Easing function for smoothness
                })
              }}
              aria-label="מעבר להמשך הסיפור"
            >
              <Typography variant="srOnly" component="p">
                גלול להמשך הסיפור
              </Typography>
              <MoreInfoWhite />
            </StyledArrowDownWrap>
          ),
          variant: FloatingTextVariant.ARROW_DOWN,
          start: 0,
          end: 10,
        },
      ],
    },
    {
      containerProps: {
        height: [280, 280],
        width: [318, 501],
        top: [20, 10],
      },
      children: [
        {
          text: brandStory.openingTitle,
          variant: FloatingTextVariant.HEADER1,
          start: 0,
          middle: 14,
          end: 60,
        },
        {
          text: brandStory.openingDescription,
          variant: FloatingTextVariant.BODY,
          start: 14,
          end: 60,
        },

        {
          text: "PREPARE TO BE AMAZED",
          variant: FloatingTextVariant.SUBTITLE,
          start: 14,
          end: 60,
        },
      ],
    },
    /////////////// 2. Clay Description
    // הסיפור מתחיל בחימר
    {
      containerProps: {
        height: [280, 280],
        width: [318, 501],
        top: [35, 25],
      },
      children: [
        {
          text: brandStory.clayTitle,
          variant: FloatingTextVariant.HEADER1HEB,
          start: 60,
          middle: 70,
          end: 105,
        },
        {
          text: brandStory.clayDescription,
          variant: FloatingTextVariant.BODY,
          start: 70,
          end: 105,
        },
      ],
    },
    /////////////// 3.Movement
    // problem here!
    // נשמה בתנועה
    {
      containerProps: {
        height: [271, 273],
        width: [320, 488],
        top: [20, 10],
      },

      children: [
        {
          text: (
            <StyledJapCont>
              <KodoLeft />
              <KodoRight />
            </StyledJapCont>
          ),
          variant: FloatingTextVariant.SUBTITLE,
          start: 106,
          middle: 125,
          end: 152,
        },
        {
          text: "KODO",
          variant: FloatingTextVariant.SUBTITLE,
          start: 106,
          middle: 125,
          end: 152,
        },
        {
          text: brandStory.movementTitle,
          variant: FloatingTextVariant.HEADER1HEB,
          start: 125,
          middle: 135,
          end: 152,
        },
        {
          text: brandStory.movementDescription,
          variant: FloatingTextVariant.BODY,
          start: 125,
          middle: 135,
          end: 152,
        },
      ],
    },
    //////////////// 4.Inside beauty
    // יופי פנימי
    {
      containerProps: {
        height: [205, 176],
        width: [320, 488],
        top: [0, 10],
      },

      children: [
        {
          text: brandStory.insideBeautyTitle,
          variant: FloatingTextVariant.HEADER1HEB,
          start: 162,
          middle: 178,
          end: 224,
        },
        {
          text: brandStory.insideBeautyDescription,
          variant: FloatingTextVariant.BODY,
          start: 178,
          end: 224,
        },
      ],
    },
    /////////////// 5. Safety
    // הביטחון להינות
    {
      containerProps: {
        height: [180, 176],
        width: [320, 492],
        top: [35, 25],
      },

      children: [
        {
          text: brandStory.safetyTitle,
          variant: FloatingTextVariant.HEADER2,
          start: 280,
          middle: 295,
          end: 323,
        },
        {
          text: brandStory.safetyDescription,
          variant: FloatingTextVariant.BODY,
          start: 289,
          end: 323,
        },
      ],
    },
    /////////////// 6.Driving Experience
    // חווית נהיגה
    {
      containerProps: {
        height: [240, 298],
        width: [320, 459],
        top: [20, 10],
      },
      children: [
        {
          text: (
            <StyledJanboWrap>
              <Janbo />
            </StyledJanboWrap>
          ),
          variant: FloatingTextVariant.SUBTITLE,
          start: 323,
          middle: 342,
          end: 380,
        },
        {
          text: "JINBA ITTAI",
          variant: FloatingTextVariant.HEADER2,
          start: 323,
          middle: 342,
          end: 380,
        },
        {
          text: brandStory.drivingExperienceTitle,
          variant: FloatingTextVariant.HEADER1HEB,
          start: 328,
          middle: 342,
          end: 380,
        },
        {
          text: brandStory.drivingExperienceDescription,
          variant: FloatingTextVariant.BODY,
          start: 332,
          end: 380,
        },
      ],
    },
  ]

  return (
    <MintStory
      textData={textData}
      mobilePath={`${API_URL}/Uploads/story/mobile/Mazda_Laatar_306983_856x1460_fix`}
      desktopPath={`${API_URL}/Uploads/story/desktop/Mazda_Laatar_306983_1920x940_fix`}
      headerSize={[HEADER_ANIME_M, HEADER_ANIME_D]}
      // headerSize={[HEADER_HEIGHT_M, HEADER_HEIGHT_D]}
      framesCount={422}
      extraPadding={14000}
      onAlmostComplete={resetSwiper}
      speedUpFrom={412}
      scrollBtnRef={scrollBtnRef}
    />
  )
}

export default Story
